import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useToast } from "../components/toast-container";
import { getApiErrorMessage, ResultCode } from "../lib/result-codes";
import type { BaseResponse } from "../lib/api-contract";

type ResultLike = Pick<BaseResponse<unknown>, "success" | "message" | "user_message"> & {
  code?: number;
};

interface UseResultHandlerOptions {
  edgeCases?: Partial<Record<number, () => void>>;
  successMessage?: string;
  errorMessage?: string;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export const useResultHandler = () => {
  const { addToast } = useToast();
  const { t } = useTranslation();

  const showToast = useCallback(
    (type: "success" | "error", message: string) => {
      addToast({
        type,
        title: t(type === "success" ? "common.success" : "common.error"),
        message,
      });
    },
    [addToast, t],
  );

  const handleResult = useCallback(
    (result: ResultLike, options: UseResultHandlerOptions = {}) => {
      const edgeCaseHandler =
        result.code === undefined ? undefined : options.edgeCases?.[result.code];

      if (edgeCaseHandler) {
        edgeCaseHandler();
        return result.success ?? result.code === ResultCode.Success;
      }

      const isSuccess = result.success ?? result.code === ResultCode.Success;
      const shouldShowToast = isSuccess
        ? options.showSuccessToast !== false
        : options.showErrorToast !== false;

      if (shouldShowToast) {
        showToast(
          isSuccess ? "success" : "error",
          isSuccess
            ? options.successMessage || result.user_message || result.message || t("common.success")
            : options.errorMessage || result.user_message || result.message || t("common.error"),
        );
      }

      return isSuccess;
    },
    [showToast, t],
  );

  const handleError = useCallback(
    (error: unknown, fallback?: string) => {
      const message = getApiErrorMessage(error, fallback || t("common.error"));
      showToast("error", message);
      return message;
    },
    [showToast, t],
  );

  const handleMessage = useCallback(
    (message: string, type: "success" | "error" = "error") => {
      showToast(type, message);
    },
    [showToast],
  );

  return { handleResult, handleError, handleMessage };
};
